import { Paper } from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import SearchBoxCompo from "../../components/Comman/SearchBoxCompo";
import TypographyCompo from "../../components/Comman/TypographyCompo";
import DynamicMainScreen from "../../components/dynamicscreens/DynamicMainScreen";

import { ChallanDetailsJson } from "../../DynamicFormsJson/MastersJSON/ChallanDetails";
import {
    getChallanDetailsBwteenDates,
    getChallanDetailsById,
} from "../../Slice/vanStockTransfer.slice";
import ChallanCard from "./components/ChallanCard";
import { transformChallan } from "./components/ChallanStaticData";
import { STATUS_CONFIG } from "./helpers/Helpers";

import {
    noChallanMatchedYourFilterMsg,
    noInternetMsg,
    serverMsg,
} from "../../config/messageconstant";
import { getCurrentDate } from "../../utils/dateFormat";
import { showLoador, showNotification } from "../Landing/Landing.slice";
import {
    emptyState,
    filterBar,
    layout,
    parentCard,
} from "./styles/ChallanStyles";

// ── Filter helper ─────────────────────────────────────────────────────────────

const filterChallans = (challans, search, statusFilter, repFilter) => {
    const q = search.toLowerCase();
    return challans.filter((c) => {
        const matchSearch =
            !q ||
            c.id.toLowerCase().includes(q) ||
            c.repName.toLowerCase().includes(q) ||
            c.beat.toLowerCase().includes(q) ||
            c.status.toLowerCase().includes(q);
        const matchStatus = !statusFilter || c.status === statusFilter;
        const matchRep = !repFilter || c.rep === repFilter;
        return matchSearch && matchStatus && matchRep;
    });
};

// ── Component ─────────────────────────────────────────────────────────────────

const ChallanDetails = ({
    getChallanDetailsBwteenDates,
    getChallanDetailsById,
    showLoador,
    showNotification,
}) => {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [repFilter, setRepFilter] = useState("");
    const [rowData, setRowData] = useState({});
    const lastSearchParams = useRef(null);


    // ── On load: fetch with today's date ─────────────────────────────────────
    React.useEffect(() => {
        const fetchData = async () => {
            const today = getCurrentDate();

            try {
                showLoador({ loador: true });

                const response = await getChallanDetailsBwteenDates({
                    fromDate: today,
                    toDate: today,
                });

                if (response && Object.keys(response).length > 0) {
                    if (response && Object.keys(response).length > 0) {
                        setRowData(response?.response ?? {});
                    }
                }
            } catch (error) {
                showNotification({
                    msg: error?.message || serverMsg,
                    severity: "error",
                });
            } finally {
                showLoador({ loador: false });
            }
        };

        fetchData();
    }, []);

    // ── Search form submit ────────────────────────────────────────────────────
    const handleSearchData = useCallback(async (dataOnSearch) => {
        const today = getCurrentDate();

        // Store the params being used (or fall back to today)
        const params = {
            fromDate: dataOnSearch?.fromDate ?? today,
            toDate: dataOnSearch?.toDate ?? today,
        };
        lastSearchParams.current = params;  // ← save it

        try {
            showLoador({ loador: true });

            const response = await getChallanDetailsBwteenDates({
                fromDate: dataOnSearch?.fromDate ?? today,
                toDate: dataOnSearch?.toDate ?? today,
            });

            if (response && Object.keys(response).length > 0) {
                setRowData(response?.response ?? {});
            }
        } catch (error) {
            showNotification({
                msg: error?.message || serverMsg,
                severity: "error",
            });
        } finally {
            showLoador({ loador: false });
        }
    }, [getChallanDetailsBwteenDates, showLoador, showNotification]);

    // ── Card expand: fetch detail by UUID ────────────────────────────────────
    // Called by ChallanCard the first time the user opens it.
    // Returns a Promise so ChallanCard can await the result and update its
    // own local body state — no global state needed for per-card detail.
    const handleExpand = useCallback((challanId) => {
        return new Promise((resolve) => {
            if (!navigator.onLine) {
                showNotification({ msg: noInternetMsg, severity: "error" });
                resolve(null);
                return;
            }
            showLoador({ loador: true });
            getChallanDetailsById({ challanId: challanId }).then(({ response, success }) => {
                if (!success) {
                    showNotification({ msg: serverMsg, severity: "error" });
                    resolve(null);
                } else {
                    resolve(response ?? null);
                }
            });
            showLoador({ loador: false });

        });
    }, [getChallanDetailsById, showLoador, showNotification]);

    // ── Transform list data ───────────────────────────────────────────────────
    const challans = useMemo(() => {
        const records = rowData?.challanDetails ?? [];
        return records.map(transformChallan);
    }, [rowData]);

    // ── Filtered list ─────────────────────────────────────────────────────────
    const filteredChallans = useMemo(
        () => filterChallans(challans, search, statusFilter, repFilter),
        [challans, search, statusFilter, repFilter]
    );

    // ── Status legend ─────────────────────────────────────────────────────────
    const renderLegend = useMemo(
        () =>
            Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <TypographyCompo key={key} style={filterBar.legendItem}>
                    <TypographyCompo style={filterBar.legendDot(cfg.dot)} />
                    {cfg.label}
                </TypographyCompo>
            )),
        []
    );

    // ── Card list ─────────────────────────────────────────────────────────────
    const renderChallanList = useMemo(() => {
        if (!filteredChallans.length) {
            return (
                <div style={emptyState.wrapper}>
                    <div style={emptyState.text}>{noChallanMatchedYourFilterMsg}</div>
                </div>
            );
        }
        return filteredChallans.map((c) => (
            <ChallanCard
                key={c.rawId}
                challan={c}
                onExpand={handleExpand}
                handleSearchData={handleSearchData}
                lastSearchParams={lastSearchParams}   // ← add this
            />
        ));
    }, [filteredChallans, handleExpand]);

    // ── JSX ───────────────────────────────────────────────────────────────────
    return (
        <Paper sx={{ p: 2, borderRadius: 0 }} elevation={0}>
            <DynamicMainScreen
                dynamicMasterData={{}}
                maxDateValid
                formPath={ChallanDetailsJson.formPath}
                showSearchBox={false}
                onSearchData={handleSearchData}
                screenTitle={ChallanDetailsJson.screenTitle}
                fieldMeta={[]}
                searchList={ChallanDetailsJson.searchList}
                showPdfDownload={false}
                showExcelDownload={false}
                showExtension={false}
                pdfFileName={false}
                tableColumnsToFilter={[]}
                tableColumns={[]}
                tableData={[]}
                getTableData={handleSearchData}
                callApi={false}
                getTableDataById={false}
                apiBaseURL={ChallanDetailsJson.apiBaseURL}
                baseIdColumn={ChallanDetailsJson.baseIdColumn}
                isNote={false}
                showDeleteIcon={false}
                showViewButtonInTable={false}
            />

            <div style={layout.page}>
                <div style={layout.container}>

                    {/* Search + Legend */}
                    <div style={filterBar.wrapper}>
                        <SearchBoxCompo
                            value={search}
                            onChange={setSearch}
                            placeholder="Search..."
                            style={{ marginTop: -55, marginBottom: 4 }}
                        />
                        <div style={filterBar.legend}>{renderLegend}</div>
                    </div>

                    {/* Parent Card */}
                    <div style={parentCard.wrapper}>
                        <div style={parentCard.header}>
                            <TypographyCompo style={parentCard.headerTitle}>
                                Challan List
                            </TypographyCompo>
                            <TypographyCompo style={parentCard.headerCount}>
                                {filteredChallans.length} challan
                                {filteredChallans.length !== 1 ? "s" : ""} found
                            </TypographyCompo>
                        </div>

                        <div style={
                            !filteredChallans.length ? parentCard.scrollBodyWhenNoChallandFound
                                : parentCard.scrollBody}
                            className="challan-scroll-body">
                            {renderChallanList}
                        </div>
                    </div>
                </div>
            </div>
        </Paper>
    );
};

// ── Redux wiring ──────────────────────────────────────────────────────────────

const mapStateToProps = (state) => ({
});

const mapDispatchToProps = {
    showNotification,
    showLoador,
    getChallanDetailsBwteenDates,
    getChallanDetailsById,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChallanDetails);